import React, {
    memo,
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Button, Icon, Loader, Modal, Tabs, Tooltip } from '@Sage/hoi-poi-ui';
import { COMPANIES, CONTACTS } from 'constants/Entities';
import { EntityFiltersActions, EntityListSelectActions } from 'actions';
import Context from 'managers/Context';
import { getLiteral } from 'utils/getLiteral';
import RecipientsAccountsTab from './RecipientsAccountsTab';
import RecipientsContactsTab from './RecipientsContactsTab';

import './styles.scss';

const TAB_ACCOUNTS = 'accounts';
const TAB_CONTACTS = 'contacts';

const INITIAL_MARKETING_STATUS_STATE = {
    [TAB_ACCOUNTS]: false,
    [TAB_CONTACTS]: false,
};

const SelectRecipientsModal = memo(
    ({
        isOpen,
        campaignId,
        token: initialToken,
        onDone,
        onCancel,
        companiesToken,
        contactsToken,
        cancelSelection,
        accountsTotal,
        contactsTotal,
        setActiveEntity,
        resetListSelect,
        syncEntityListSelectionToken,
        clearFilters,
        hideFilters,
    }) => {
        const [selectionToken, setSelectionToken] = useState(initialToken || null);
        const [selectionReadyForList, setSelectionReadyForList] = useState(false);
        const [activeTab, setActiveTab] = useState(TAB_ACCOUNTS);
        const [isDoneSubmitting, setIsDoneSubmitting] = useState(false);
        const [initialMarketingStatusApplied, setInitialMarketingStatusApplied] = useState(
            INITIAL_MARKETING_STATUS_STATE,
        );

        const isMountedRef = useRef(false);
        const selectionTokenRef = useRef('');
        const prevIsOpenRef = useRef(false);
        const entitySelectionRequestRef = useRef(0);

        useEffect(() => {
            isMountedRef.current = true;
            return () => {
                isMountedRef.current = false;
            };
        }, []);

        useLayoutEffect(() => {
            if (!isOpen) return;
            resetListSelect(COMPANIES, false);
            resetListSelect(CONTACTS, false);
            setActiveTab(TAB_ACCOUNTS);
            setActiveEntity(COMPANIES);
            clearFilters({
                entity: COMPANIES,
                filterKey: 'recipientsAccounts',
                isAPurge: false,
                refresh: false,
            });
            clearFilters({
                entity: CONTACTS,
                filterKey: 'recipientsContacts',
                isAPurge: false,
                refresh: false,
            });
            hideFilters(COMPANIES, 'recipientsAccounts');
            hideFilters(CONTACTS, 'recipientsContacts');
        }, [isOpen, resetListSelect, setActiveEntity, clearFilters, hideFilters]);

        useEffect(() => {
            if (!isOpen) {
                prevIsOpenRef.current = false;
                resetListSelect(COMPANIES, false);
                resetListSelect(CONTACTS, false);
                setSelectionReadyForList(false);
                setInitialMarketingStatusApplied(INITIAL_MARKETING_STATUS_STATE);
                entitySelectionRequestRef.current += 1;
                return;
            }

            const justOpened = !prevIsOpenRef.current;
            prevIsOpenRef.current = true;

            if (justOpened) {
                selectionTokenRef.current = initialToken || '';
            }

            setSelectionReadyForList(false);

            const entityConfig = activeTab === TAB_CONTACTS ? CONTACTS : COMPANIES;
            const entity = entityConfig.trueName;
            const requestId = ++entitySelectionRequestRef.current;

            Context.domainManager
                .sendEntityListSelection({
                    selectionForId: campaignId ? Number(campaignId) : 0,
                    selectionFor: 'marketingCampaigns',
                    entity,
                    token: selectionTokenRef.current,
                })
                .then((data) => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current)
                        return;
                    const token = data?.token || data?.Token || '';
                    selectionTokenRef.current = token;
                    setSelectionToken(token);
                    syncEntityListSelectionToken({
                        entity: entityConfig,
                        data,
                        selectionFor: 'marketingCampaigns',
                        selectionEntity: entity,
                    });
                })
                .catch(() => {
                    // proceed with empty token — user can still make a selection
                })
                .finally(() => {
                    if (!isMountedRef.current || requestId !== entitySelectionRequestRef.current) {
                        return;
                    }
                    setSelectionReadyForList(true);
                });
        }, [
            isOpen,
            activeTab,
            campaignId,
            initialToken,
            resetListSelect,
            syncEntityListSelectionToken,
        ]);

        const getPreferredToken = useCallback(() => {
            return (
                (activeTab === TAB_CONTACTS ? contactsToken : companiesToken) ||
                (activeTab === TAB_ACCOUNTS ? contactsToken : companiesToken) ||
                selectionTokenRef.current ||
                selectionToken ||
                ''
            );
        }, [activeTab, companiesToken, contactsToken, selectionToken]);

        const refreshSelectedTotals = useCallback(
            async (tokenOverride, options = {}) => {
                const { force = false, entitiesToRefresh } = options;
                const preferredToken = tokenOverride || getPreferredToken();
                const shouldRefreshAccounts = entitiesToRefresh?.accounts ?? true;
                const shouldRefreshContacts = entitiesToRefresh?.contacts ?? true;

                if (!preferredToken) {
                    return { accounts: 0, contacts: 0 };
                }

                const cachedTotals = selectionTotalsCacheRef.current[preferredToken];
                if (cachedTotals && !force && shouldRefreshAccounts && shouldRefreshContacts) {
                    return cachedTotals;
                }

                const requestId = ++selectionTotalsRequestRef.current;

                const getSelectionInfo = (entity) =>
                    Context.domainManager
                        .sendEntityListSelectionInfo({
                            token: preferredToken,
                            selectionFor: 'marketingCampaigns',
                            entity,
                        })
                        .catch(() => null);

                const [accountsInfo, contactsInfo] = await Promise.all([
                    shouldRefreshAccounts ? getSelectionInfo('accounts') : Promise.resolve(null),
                    shouldRefreshContacts ? getSelectionInfo('contacts') : Promise.resolve(null),
                ]);

                const nextTotals = {
                    accounts: shouldRefreshAccounts
                        ? Number(accountsInfo?.entities ?? accountsTotal) || 0
                        : Number(cachedTotals?.accounts ?? accountsTotal) || 0,
                    contacts: shouldRefreshContacts
                        ? Number(contactsInfo?.entities ?? contactsTotal) || 0
                        : Number(cachedTotals?.contacts ?? contactsTotal) || 0,
                };

                selectionTotalsCacheRef.current[preferredToken] = nextTotals;

                if (!isMountedRef.current || requestId !== selectionTotalsRequestRef.current) {
                    return cachedTotals || nextTotals;
                }

                return nextTotals;
            },
            [accountsTotal, contactsTotal, getPreferredToken],
        );

        const handleTabChange = useCallback(
            (key) => {
                setSelectionReadyForList(false);
                setActiveTab(key);
                const entity = key === TAB_CONTACTS ? CONTACTS : COMPANIES;
                setActiveEntity(entity);
            },
            [setActiveEntity],
        );

        const handleSelectionChange = useCallback(() => {}, []);

        const handleInitialMarketingStatusApplied = useCallback((tabKey) => {
            setInitialMarketingStatusApplied((prev) => {
                if (prev[tabKey]) {
                    return prev;
                }

                return {
                    ...prev,
                    [tabKey]: true,
                };
            });
        }, []);

        const handleDone = useCallback(async () => {
            const baseToken = selectionTokenRef.current || selectionToken || initialToken || '';
            const accountsSelectionToken = companiesToken || baseToken;
            const contactsSelectionToken = contactsToken || baseToken;
            const campaignToken = companiesToken || contactsToken || baseToken;

            if (!campaignToken) {
                onDone?.('', 0);
                return;
            }

            setIsDoneSubmitting(true);

            try {
                const getSelectionInfo = (entity, token) => {
                    if (!token) return Promise.resolve(null);

                    return Context.domainManager
                        .sendEntityListSelectionInfo({
                            token,
                            selectionFor: 'marketingCampaigns',
                            entity,
                        })
                        .catch(() => null);
                };

                const [accountsInfo, contactsInfo] = await Promise.all([
                    getSelectionInfo('accounts', accountsSelectionToken),
                    getSelectionInfo('contacts', contactsSelectionToken),
                ]);

                const nextTotals = {
                    accounts: Number(accountsInfo?.entities ?? accountsTotal) || 0,
                    contacts: Number(contactsInfo?.entities ?? contactsTotal) || 0,
                };
                const nextCount =
                    (Number(nextTotals?.accounts ?? accountsTotal) || 0) +
                    (Number(nextTotals?.contacts ?? contactsTotal) || 0);

                selectionTokenRef.current = campaignToken;
                setSelectionToken(campaignToken);
                onDone?.(campaignToken, nextCount);
            } finally {
                if (isMountedRef.current) {
                    setIsDoneSubmitting(false);
                }
            }
        }, [
            accountsTotal,
            companiesToken,
            contactsTotal,
            contactsToken,
            initialToken,
            onDone,
            selectionToken,
        ]);

        const handleCancel = useCallback(() => {
            cancelSelection();
            onCancel?.();
        }, [cancelSelection, onCancel]);

        const isDoneDisabled = isDoneSubmitting;

        const tabs = useMemo(() => {
            const tabIconTitle = (icon, label, isActive) => (
                <Tooltip content={label} placement="top">
                    <div
                        className={`fm-select-recipients-modal__tab-title${isActive ? ' fm-select-recipients-modal__tab-title--active' : ''}`}
                        aria-label={label}
                    >
                        <Icon
                            name={icon}
                            size="large"
                            className="fm-select-recipients-modal__tab-icon"
                        />
                    </div>
                </Tooltip>
            );

            const tabLoader = (
                <div className="fm-select-recipients-modal__tab-loader">
                    <Loader />
                </div>
            );

            let accountsTabContent = null;
            if (activeTab === TAB_ACCOUNTS) {
                accountsTabContent = selectionReadyForList ? (
                    <RecipientsAccountsTab
                        selectionToken={selectionToken}
                        onSelectionChange={handleSelectionChange}
                        shouldApplyInitialMarketingStatus={
                            !initialMarketingStatusApplied[TAB_ACCOUNTS]
                        }
                        onInitialMarketingStatusApplied={handleInitialMarketingStatusApplied}
                    />
                ) : (
                    tabLoader
                );
            }

            let contactsTabContent = null;
            if (activeTab === TAB_CONTACTS) {
                contactsTabContent = selectionReadyForList ? (
                    <RecipientsContactsTab
                        selectionToken={selectionToken}
                        onSelectionChange={handleSelectionChange}
                        shouldApplyInitialMarketingStatus={
                            !initialMarketingStatusApplied[TAB_CONTACTS]
                        }
                        onInitialMarketingStatusApplied={handleInitialMarketingStatusApplied}
                    />
                ) : (
                    tabLoader
                );
            }

            return [
                {
                    key: TAB_ACCOUNTS,
                    title: tabIconTitle(
                        'accounts',
                        getLiteral('label_accounts'),
                        activeTab === TAB_ACCOUNTS,
                    ),
                    content: accountsTabContent,
                },
                {
                    key: TAB_CONTACTS,
                    title: tabIconTitle(
                        'contacts',
                        getLiteral('label_contacts'),
                        activeTab === TAB_CONTACTS,
                    ),
                    content: contactsTabContent,
                },
            ];
        }, [
            activeTab,
            handleInitialMarketingStatusApplied,
            handleSelectionChange,
            initialMarketingStatusApplied,
            selectionToken,
            selectionReadyForList,
        ]);

        if (!isOpen) return null;

        return (
            <Modal
                className="fm-select-recipients-modal"
                isOpen={isOpen}
                size="full"
                useContentStaticHeight
                title={getLiteral('label_select_recipients')}
                onRequestClose={handleCancel}
                useCornerClose={true}
                overrides={{
                    content: {
                        className: 'fm-select-recipients-modal__content',
                    },
                }}
            >
                <Tabs
                    className="fm-select-recipients-modal__tabs"
                    tabs={tabs}
                    activeKey={activeTab}
                    onChange={handleTabChange}
                    editable={false}
                />
                <div className="fm-select-recipients-modal__footer">
                    <Button type="secondary" onClick={handleCancel}>
                        {getLiteral('action_cancel')}
                    </Button>
                    <Tooltip content={undefined} placement="top">
                        <div className="fm-select-recipients-modal__done-wrapper">
                            <Button type="primary" isDisabled={isDoneDisabled} onClick={handleDone}>
                                {getLiteral('action_done')}
                            </Button>
                        </div>
                    </Tooltip>
                </div>
            </Modal>
        );
    },
);

SelectRecipientsModal.displayName = 'SelectRecipientsModal';

SelectRecipientsModal.propTypes = {
    isOpen: PropTypes.bool,
    campaignId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    token: PropTypes.string,
    onDone: PropTypes.func,
    onCancel: PropTypes.func,
};

function mapStateToProps(state) {
    const companies = state.entityListSelect?.[COMPANIES.entity];
    const contacts = state.entityListSelect?.[CONTACTS.entity];
    return {
        companiesToken: companies?.token || '',
        contactsToken: contacts?.token || '',
        accountsTotal: Number(companies?.total ?? 0) || 0,
        contactsTotal: Number(contacts?.total ?? 0) || 0,
    };
}

const boundEntityListSelect = (dispatch) => bindActionCreators(EntityListSelectActions, dispatch);

const mapDispatchToProps = (dispatch) => {
    const entityListSelect = boundEntityListSelect(dispatch);
    const entityFilters = bindActionCreators(EntityFiltersActions, dispatch);
    return {
        cancelSelection: entityListSelect.cancelSelection,
        setActiveEntity: entityListSelect.setActive,
        resetListSelect: entityListSelect.resetListSelect,
        syncEntityListSelectionToken: entityListSelect.syncEntityListSelectionToken,
        clearFilters: entityFilters.clearFilters,
        hideFilters: entityFilters.hideFilters,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SelectRecipientsModal);
